import { CARD_PURPOSE } from '~/actions/actionTypes';
import {
  EOnboardingComponents,
  EPrefillableComponents,
  ESignupComponents,
  IErrorState,
  IOnboardingState,
  ISignupState,
  IState,
} from '~/sharedInterfaces/IState';
import { IPage, ISettings } from '~/sharedInterfaces/SharedInterfaces';
import { getLocale } from '~/utils';

const WIDGET_STATE_KEY = 'widgetState';

export const deletePersistedState = (widgetId: string) => {
  try {
    localStorage.removeItem(`${widgetId}-${WIDGET_STATE_KEY}`);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('An error occured when trying to remove persisted state:', error);
  }
};

const getPersistedState = (widgetId: string) => {
  try {
    const serializedState = localStorage.getItem(`${widgetId}-${WIDGET_STATE_KEY}`);
    if (!serializedState) return undefined;
    deletePersistedState(widgetId);
    return JSON.parse(serializedState);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('An error occured when getting persisted state:', error);
    return undefined;
  }
};

export const setPersistedState = (widgetId: string, state: IState) => {
  try {
    const serializedState = JSON.stringify(state);
    localStorage.setItem(`${widgetId}-${WIDGET_STATE_KEY}`, serializedState);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('An error occured when setting persisted state:', error);
  }
};

const createComponentState = (pages: IPage[]) => {
  let onboardingInitialState: IOnboardingState = {};
  let signupInitialState: ISignupState = {};
  let initialErrorState: IErrorState = { serverError: '', default: '' };

  pages.forEach((page: any) => {
    Object.keys(page).forEach((componentKey: any) => {
      if (
        Object.values(ESignupComponents).some(
          (signupComponentKey) => signupComponentKey === componentKey
        )
      ) {
        if (componentKey === CARD_PURPOSE) {
          signupInitialState = {
            ...signupInitialState,
            cardPurpose: {
              applicationReasons: [{ reason: '', reasonText: null }],
            },
          };
        } else {
          signupInitialState = {
            ...signupInitialState,
            [componentKey]:
              page[componentKey].prefill &&
              Object.values(EPrefillableComponents).includes(componentKey)
                ? page[componentKey].prefill
                : '',
          };
        }
      } else if (
        Object.values(EOnboardingComponents).some(
          (onboardingComponentKey) => onboardingComponentKey === componentKey
        )
      ) {
        onboardingInitialState = {
          ...onboardingInitialState,
          [componentKey]:
            page[componentKey].prefill &&
            Object.values(EPrefillableComponents).includes(componentKey)
              ? page[componentKey].prefill
              : '',
        };
      }
    });
  });

  const setCorrectError = (key: string) => {
    if (key === 'orgNumber' || key === 'loanTerm' || key === 'emailAddress') {
      initialErrorState = { ...initialErrorState, [key]: { missing: '', invalid: '' } };
    } else {
      initialErrorState = { ...initialErrorState, [key]: { missing: '' } };
    }
  };

  Object.keys(signupInitialState).forEach((key) => {
    setCorrectError(key);
  });

  Object.keys(onboardingInitialState).forEach((key) => {
    setCorrectError(key);
  });

  return { onboardingInitialState, signupInitialState, initialErrorState };
};

const initialState = (pages: IPage[], settings: ISettings, sessionId: string): IState => {
  const { onboardingInitialState, signupInitialState, initialErrorState } = createComponentState(
    pages
  );
  const locale = getLocale(settings);
  return {
    signup: signupInitialState,
    onboarding: onboardingInitialState,
    error: initialErrorState,
    widgetContext: {
      pages,
      currentPage: 0,
      settings,
      locale,
      sessionId,
      signupCompleted: false,
      signupNewCustomer: null,
      cardApplicationId: '',
      widgetFinished: false,
      loading: false,
      isInitiated: false,
      accessToken: '',
      clientId: 0,
    },
  };
};

const setSessionInCookie = (sessionId: string) => {
  const cookieExpiration = new Date();
  const oneDayForward = 24 * 3600000;
  cookieExpiration.setTime(cookieExpiration.getTime() + oneDayForward);
  document.cookie = `qredWidgetSessionId=${sessionId};expires=${cookieExpiration.toUTCString()};`;
};

const getCookie = (name: string) => {
  const cookiePairs = document.cookie.split(';');
  const cookie = cookiePairs.find((pair: string) => {
    const cookiePair = pair.split('=');
    if (name === cookiePair[0].trim()) return true;
    return false;
  });
  return cookie?.split('=')[1].trim();
};

export const getGATrafficCookie = () => getCookie('_ga');

export const getSessionFromCookie = () => {
  return getCookie('qredWidgetSessionId');
};

export const createSession = () => {
  const sessionId = `${Math.random()}${Date.now()}`.replace('0.', '');
  setSessionInCookie(sessionId);
  return sessionId;
};

const checkForInconsistency = (defaultState: IState, persistedState: IState): boolean => {
  if (!persistedState) return false;
  if (persistedState.widgetContext.pages.length !== defaultState.widgetContext.pages.length)
    return false;
  if (persistedState.widgetContext.sessionId !== defaultState.widgetContext.sessionId) return false;
  return true;
};

export const getPreLoadedState = (pages: IPage[], settings: ISettings, sessionId: string) => {
  const persistedState = getPersistedState(settings.id);
  const defaultState = initialState(pages, settings, sessionId);
  return checkForInconsistency(defaultState, persistedState) ? persistedState : defaultState;
};
